import { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Eye, FileText, Pencil, Save } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { BlockEditor } from '../BlockEditor';
import { EmailPreview } from '../EmailPreview';
import { SalvaModelloDialog } from '../modelli/SalvaModelloDialog';
import { SceltaModelloDialog } from '../modelli/SceltaModelloDialog';
import type { ConfigInvio, EmailAutomationStep } from '../../../types/automazioni.types';
import type { DocumentoEmail } from '@email/types.ts';

interface ComponiEmailModalProps {
  aperto: boolean;
  passo: EmailAutomationStep;
  onChangeConfig: (parziale: Partial<ConfigInvio>) => void;
  onChiudi: (aperto: boolean) => void;
}

/**
 * La finestra in cui si compone la email di una casella.
 *
 * Tre fasce che non si pestano i piedi: la testata con il nome della casella,
 * il mezzo che scorre (la tela o l'anteprima), il fondo con il bottone.
 * L'oggetto sta in alto insieme alle linguette, fermo: si scrive la email
 * sapendo cosa si sta scrivendo, e si passa all'anteprima senza dover prima
 * risalire tutto.
 */
export function ComponiEmailModal({
  aperto,
  passo,
  onChangeConfig,
  onChiudi,
}: ComponiEmailModalProps) {
  const [salvaModelloAperta, setSalvaModelloAperta] = useState(false);
  const [sceltaModelloAperta, setSceltaModelloAperta] = useState(false);

  const documento = passo.kind === 'invio' ? (passo.config as ConfigInvio).document : null;
  const vuota = (documento?.blocchi?.length ?? 0) === 0;

  return (
    <Dialog open={aperto} onOpenChange={onChiudi}>
      {/* Altezza fissa e non massima: passando da Componi ad Anteprima la
          finestra non cambia dimensione sotto il cursore. Lo scorrimento sta
          tutto nel DialogBody -- `overflow-y-hidden` toglie quello che il
          componente mette di suo, altrimenti scorrerebbero via anche il
          titolo, le linguette e il bottone di chiusura. */}
      <DialogContent className="max-w-6xl sm:h-[88vh] flex flex-col overflow-y-hidden gap-0">
        <DialogHeader className="flex-shrink-0 pb-3 border-b border-gray-100">
          <DialogTitle>Componi la email</DialogTitle>
          <p className="text-xs text-neutral-500">
            Casella <strong className="font-semibold">{passo.label || 'senza nome'}</strong> ·
            il logo in cima e i dati aziendali in fondo si aggiungono da soli
          </p>
        </DialogHeader>

        {passo.kind === 'invio' && documento && (
          <Tabs.Root
            defaultValue="componi"
            className="flex flex-col flex-1 min-h-0 overflow-hidden"
          >
            {/* Oggetto e linguette restano fermi: si cambia vista senza
                dover prima risalire tutta la email. */}
            <div className="flex flex-col gap-3 py-3 flex-shrink-0 sm:flex-row sm:items-end sm:justify-between">
              {/* Stretti, uno sotto l'altro: affiancati il campo si riduce a
                  una fessura in cui l'oggetto non si legge. */}
              <div className="min-w-0 sm:flex-1 sm:max-w-md">
                <Label htmlFor="oggetto-modale" className="text-xs">
                  Oggetto
                </Label>
                <Input
                  id="oggetto-modale"
                  value={(passo.config as ConfigInvio).subject ?? ''}
                  onChange={(e) =>
        onChangeConfig({ subject: e.target.value })
                  }
                  placeholder="Quello che si legge nella posta in arrivo"
                  className="mt-1"
                />
              </div>

              <Tabs.List className="inline-flex self-start rounded-lg border border-gray-200 bg-white p-1 flex-shrink-0 sm:self-auto">
                <Tabs.Trigger
                  value="componi"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-neutral-500 data-[state=active]:bg-primary-600 data-[state=active]:text-white transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Componi
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="anteprima"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-neutral-500 data-[state=active]:bg-primary-600 data-[state=active]:text-white transition-colors"
                >
                  <Eye className="w-3.5 h-3.5" />
                  Anteprima
                </Tabs.Trigger>
              </Tabs.List>
            </div>

            <DialogBody className="border-t border-gray-100 pt-3">
              <Tabs.Content value="componi" className="outline-none">
                <BlockEditor
                  documento={documento}
                  onChange={(documento: DocumentoEmail) =>
onChangeConfig({ document: documento })
                  }
                />
              </Tabs.Content>

              <Tabs.Content value="anteprima" className="outline-none h-full">
                <EmailPreview
                  documento={documento}
                  oggetto={(passo.config as ConfigInvio).subject ?? ''}
                  preheader={(passo.config as ConfigInvio).preheader ?? ''}
                  adatta
                />
              </Tabs.Content>
            </DialogBody>
          </Tabs.Root>
        )}

        <DialogFooter className="flex-shrink-0 border-t border-gray-100 pt-3">
          <div className="flex flex-col-reverse gap-2 sm:mr-auto sm:flex-row sm:items-center">
            <Button variant="outline" onClick={() => setSceltaModelloAperta(true)}>
              <FileText className="w-4 h-4 mr-2" />
              Parti da un modello
            </Button>
            <Button
              variant="outline"
              onClick={() => setSalvaModelloAperta(true)}
              disabled={vuota}
            >
              <Save className="w-4 h-4 mr-2" />
              Salva come modello
            </Button>
          </div>
          <Button
            onClick={() => onChiudi(false)}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            Fatto
          </Button>
        </DialogFooter>
      </DialogContent>

      {documento && (
        <>
          <SalvaModelloDialog
            aperto={salvaModelloAperta}
            onChiudi={setSalvaModelloAperta}
            documento={documento}
            oggetto={(passo.config as ConfigInvio).subject ?? ''}
            preheader={(passo.config as ConfigInvio).preheader ?? null}
            nomeProposto={passo.label ?? ''}
            ambitoProposto="campagne"
          />

          <SceltaModelloDialog
            aperto={sceltaModelloAperta}
            onChiudi={setSceltaModelloAperta}
            ambito="campagne"
            sovrascrive={!vuota}
            onScelto={(modello) => {
              const oggettoAttuale = (passo.config as ConfigInvio).subject ?? '';
              onChangeConfig({
                document: modello.document,
                // Un oggetto gia' scritto vale piu' di quello del modello:
                // chi l'ha scritto lo ha pensato per questa casella.
                ...(oggettoAttuale.trim() ? {} : { subject: modello.subject }),
              });
            }}
          />
        </>
      )}
    </Dialog>
  );
}
